// ─────────────────────────────────────────────────────────────
//  useDicomDownload.js
//  Drop-in hook + DownloadProgressPanel component
//
//  Usage:
//    import { useDicomDownload, DownloadProgressPanel } from './useDicomDownload';
//
//    function YourComponent() {
//      const { downloadDicom, downloads } = useDicomDownload();
//      return (
//        <>
//          <button onClick={() => downloadDicom(studyInstanceUID)}>Download</button>
//          <DownloadProgressPanel downloads={downloads} />
//        </>
//      );
//    }
// ─────────────────────────────────────────────────────────────

import { useState, useRef, useCallback } from "react";
import {
  AWS_Access_Key_Id,
  AWS_Access_Key_Id_Encypt,
  AWS_Bucket_Name,
  AWS_Region,
  AWS_Secret_Access_Key,
  AWS_Secret_Access_Key_Encypt,
} from "../constants/bucketCostant";

import {
  BlockChain,
  DRCoreApi,
  DownloadApi,
  FileLoader,
  GetTokenApi,
  UploadApi,
  ViewerURL,
  keyImages,
} from "../constants/Constants";

// const DownloadApi = process.env.REACT_APP_DOWNLOAD_API || "";
const POLL_INTERVAL_MS = 1200;


// ── Helpers ──────────────────────────────────────────────────

// function AWS_Bucket_Name() {
//   return process.env.REACT_APP_AWS_BUCKET || "";
// }
// function AWS_Region() {
//   return process.env.REACT_APP_AWS_REGION || "";
// }

// ── Hook ─────────────────────────────────────────────────────

/**
 * Returns:
 *   downloadDicom(studyInstanceUID, e?)  – start a download
 *   downloads                            – array of live download states
 *   removeDownload(jobId)                – dismiss a finished/errored card
 */
export function useDicomDownload() {
  const [downloads, setDownloads] = useState([]); // [{jobId, uid, ...statusFields}]
  const pollersRef = useRef({});                   // { jobId: intervalId }

  const upsert = (jobId, patch) =>
    setDownloads((prev) =>
      prev.map((d) => (d.jobId === jobId ? { ...d, ...patch } : d))
    );

  const stopPolling = (jobId) => {
    if (pollersRef.current[jobId]) {
      clearInterval(pollersRef.current[jobId]);
      delete pollersRef.current[jobId];
    }
  };

  const triggerBrowserDownload = async (jobId, filename) => {
    try {
      const res = await fetch(`${DownloadApi}/dicomzip/download/${jobId}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || `${jobId}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      upsert(jobId, { status: "saved" });
    } catch (err) {
      upsert(jobId, { status: "error", error: `Download failed: ${err.message}` });
    }
  };

  const pollStatus = (jobId) => {
    const id = setInterval(async () => {
      try {
        const res = await fetch(`${DownloadApi}/dicomzip/status/${jobId}`);
        const data = await res.json();

        upsert(jobId, {
          status: data.status,
          progress: data.progress ?? 0,
          message: data.message ?? "",
          totalFiles: data.total_files ?? 0,
          downloadedFiles: data.downloaded_files ?? 0,
          currentFile: data.current_file ?? "",
          error: data.error ?? null,
        });

        if (data.status === "done") {
          stopPolling(jobId);
          triggerBrowserDownload(jobId, data.filename);
        } else if (data.status === "error") {
          stopPolling(jobId);
        }
      } catch (err) {
        stopPolling(jobId);
        upsert(jobId, { status: "error", error: `Polling failed: ${err.message}` });
      }
    }, POLL_INTERVAL_MS);

    pollersRef.current[jobId] = id;
  };

  const downloadDicom = useCallback(async (studyInstanceUID, _e) => {
    const tempJobId = `local-${Date.now()}`;

    // Add a placeholder immediately so the user sees feedback right away
    setDownloads((prev) => [
      {
        jobId: tempJobId,
        uid: studyInstanceUID,
        status: "starting",
        progress: 0,
        message: "Connecting…",
        totalFiles: 0,
        downloadedFiles: 0,
        currentFile: "",
        error: null,
        startedAt: new Date(),
      },
      ...prev,
    ]);

    try {
      const url =
        `${DownloadApi}/dicomzip/start` +
        `?Bucket=${encodeURIComponent(AWS_Bucket_Name())}` +
        `&path=${encodeURIComponent(studyInstanceUID)}` +
        `&region=${encodeURIComponent(AWS_Region())}`;

      const res = await fetch(url);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      const { job_id } = await res.json();

      // Replace placeholder with real job_id
      setDownloads((prev) =>
        prev.map((d) =>
          d.jobId === tempJobId ? { ...d, jobId: job_id, status: "pending" } : d
        )
      );
      pollStatus(job_id);
    } catch (err) {
      setDownloads((prev) =>
        prev.map((d) =>
          d.jobId === tempJobId
            ? { ...d, status: "error", error: err.message }
            : d
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const removeDownload = useCallback((jobId) => {
    stopPolling(jobId);
    setDownloads((prev) => prev.filter((d) => d.jobId !== jobId));
  }, []);

  return { downloadDicom, downloads, removeDownload };
}

// ── Progress Panel Component ──────────────────────────────────

const STATUS_META = {
  starting:    { label: "Starting",    color: "#6B7280", barColor: "#6B7280" },
  pending:     { label: "Queued",      color: "#F59E0B", barColor: "#F59E0B" },
  processing:  { label: "Downloading", color: "#3B82F6", barColor: "#3B82F6" },
  done:        { label: "Zipping",     color: "#8B5CF6", barColor: "#8B5CF6" },
  saved:       { label: "Saved ✓",     color: "#10B981", barColor: "#10B981" },
  error:       { label: "Failed",      color: "#EF4444", barColor: "#EF4444" },
};

const styles = {
  panel: {
    position: "fixed",
    bottom: "24px",
    left: "24px",
    zIndex: 9999,
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    maxWidth: "360px",
    width: "100%",
    fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
  },
  card: {
    background: "#0F172A",
    border: "1px solid #1E293B",
    borderRadius: "12px",
    padding: "14px 16px",
    boxShadow: "0 8px 32px rgba(0,0,0,0.45)",
    color: "#E2E8F0",
    position: "relative",
    overflow: "hidden",
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "8px",
  },
  uid: {
    fontSize: "11px",
    color: "#94A3B8",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "230px",
  },
  badge: (color) => ({
    fontSize: "10px",
    fontWeight: 700,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    color,
    padding: "2px 8px",
    borderRadius: "999px",
    border: `1px solid ${color}44`,
    background: `${color}18`,
    flexShrink: 0,
  }),
  closeBtn: {
    background: "none",
    border: "none",
    color: "#475569",
    cursor: "pointer",
    fontSize: "16px",
    lineHeight: 1,
    padding: "0 0 0 6px",
    flexShrink: 0,
  },
  message: {
    fontSize: "12px",
    color: "#94A3B8",
    marginBottom: "8px",
    minHeight: "16px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  trackOuter: {
    height: "4px",
    borderRadius: "2px",
    background: "#1E293B",
    overflow: "hidden",
  },
  trackInner: (pct, color, animated) => ({
    height: "100%",
    width: `${pct}%`,
    borderRadius: "2px",
    background: color,
    transition: "width 0.4s ease",
    backgroundImage: animated
      ? `linear-gradient(90deg, transparent 0%, ${color}99 50%, transparent 100%)`
      : "none",
    backgroundSize: animated ? "200% 100%" : "auto",
    animation: animated ? "shimmer 1.5s infinite" : "none",
  }),
  fileMeta: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: "6px",
    fontSize: "11px",
    color: "#475569",
  },
  shimmerKeyframes: `
    @keyframes shimmer {
      0%   { background-position: -200% 0; }
      100% { background-position:  200% 0; }
    }
  `,
};

export function DownloadProgressPanel({ downloads, removeDownload }) {
  if (!downloads.length) return null;

  const isFinished = (s) => s === "saved" || s === "error";
  const isActive   = (s) => s === "processing" || s === "starting" || s === "pending";

  return (
    <>
      <style>{styles.shimmerKeyframes}</style>
      <div style={styles.panel}>
        {downloads.map((dl) => {
          const meta = STATUS_META[dl.status] || STATUS_META.pending;
          const pct  = dl.status === "saved" ? 100 : dl.progress ?? 0;
          const animated = isActive(dl.status);

          return (
            <div key={dl.jobId} style={styles.card}>
              {/* Header row */}
              <div style={styles.cardHeader}>
                <span style={styles.uid} title={dl.uid}>
                  📂 {dl.uid || dl.jobId}
                </span>
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <span style={styles.badge(meta.color)}>{meta.label}</span>
                  {isFinished(dl.status) && (
                    <button
                      style={styles.closeBtn}
                      onClick={() => removeDownload(dl.jobId)}
                      title="Dismiss"
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>

              {/* Message */}
              <div style={styles.message}>
                {dl.error
                  ? `⚠ ${dl.error}`
                  : dl.currentFile
                  ? `↓ ${dl.currentFile}`
                  : dl.message}
              </div>

              {/* Progress bar */}
              <div style={styles.trackOuter}>
                <div style={styles.trackInner(pct, meta.barColor, animated)} />
              </div>

              {/* File count + percent */}
              <div style={styles.fileMeta}>
                <span>
                  {dl.totalFiles > 0
                    ? `${dl.downloadedFiles} / ${dl.totalFiles} files`
                    : dl.status === "saved"
                    ? `${dl.totalFiles} files`
                    : "Scanning…"}
                </span>
                <span>{pct}%</span>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}